/**
 * スポットブロック入札 CSV の Step1 記入ガイド表 (spec `spot-bid-block-csv-import.md`
 * §3「画面表示（記入ガイド表）」、common.md AC-DUC-037)。
 *
 * 文言の正本は spec §3 の 3 表 (通常ブロック 56 列 / リンクブロック 58 列 /
 * ループ 58 列)。 Des 回答 (#8786) により表は種別ごとに分けて並べる。 括弧は
 * design 規約 (半角 + 前に半角スペース 1 つ) に合わせて半角化する以外は逐語。
 * 列名は `~/models/bidCsvHeader` のヘッダー定数と一致させる (コマ列は 48 列を
 * 「コマ1〜コマ48」の 1 行にまとめる)。
 */
import type { BlockBidCsvKind } from '~/models/bidCsvHeader'
import type { ColumnGuideEntry } from '~/models/columnGuide'

const AREA_LIST =
  '北海道 / 東北 / 東京 / 中部 / 北陸 / 関西 / 中国 / 四国 / 九州'

const DELIVERY_DATE_BASE =
  '入札締切 (受渡日の前日 10:00) を過ぎた受渡日は入力できない。入札受付開始 (受渡日の 11 日前の 0:00) より前に提出された札は予約入札として保持され、受付開始時に JEPX へ送信される。1 つのファイルに複数の受渡日を入力できる。'
const DELIVERY_DATE_EXAMPLE =
  '翌日の日付 (当日 10:00 まで。例: 本日が 2026/08/31 なら 2026/09/01)'

// 価格の入力形式。 種別ごとに「価格はブロックに 1 つ」の一文をこの 2 つの間に挟む。
const PRICE_RANGE = '0.01〜999 の範囲で、小数点以下 2 桁まで入力。'
const PRICE_NOTES =
  '成行は指定できない。手数料自動考慮が OFF の場合は、入力した価格を入札価格として JEPX に入札する。ON の場合は、手数料を含めて売買したい価格 (希望価格) を入力する。JEPX へは手数料を加減算した入札価格で入札し、取込前の確認画面に入札価格を表示する。入札制限の上下限価格は入札価格で判定する。'

const EMISSION_BASE =
  '特定契約の電源の排出係数を紐付ける場合のみ「1」を入力 (会社情報に登録小売電気事業者 登録番号の登録が必要)。紐付けない場合は空欄にする。'

const JEPX_FREE_TEXT_BASE =
  '排出係数紐付けが「1」の場合は、判別コメントに使うアルファベットを大文字半角 1文字 (A〜Z) で入力 (「[特定契約＋登録小売電気事業者 登録番号＋アルファベット]」をシステムが組み立てて JEPX へ送信する。登録小売電気事業者 登録番号はシステム管理画面の会社情報で確認できる)。排出係数紐付けが空欄の場合は、自由な文を 70文字以内で入力。排出係数紐付けが「1」の場合は自由記述を併記できない。'

const SLOT_BASE =
  '入札するコマに 0.1〜9999.9 の範囲で、小数点以下 1 桁まで入力 (符号は付けない)。ブロックに含めないコマは空欄にする。連続した 4 コマ (2 時間) 以上が必要で、途中に空欄は入れられない。ブロックの途中で入札しないコマには 0 を入力できるが、最初と最後のコマは 0 にできない。コマごとに違う入札量を入力できる。入札制限の上限数量の範囲内で入力。'

type PairWording = {
  /** 「親と子の行には」「A と B の行には」など、ペア内で揃える旨の主語。 */
  pair: string
  /** 「親と子で別々に」「A と B で別々に」。 */
  separately: string
}

function pairedGuide(
  w: PairWording,
  extra: {
    pairNumber: ColumnGuideEntry
    blockType: ColumnGuideEntry
    area: ColumnGuideEntry
    memoExample: string
    slotOverlap: string
  },
): ColumnGuideEntry[] {
  return [
    {
      name: '受渡日',
      required: 'required',
      description: '入札する受渡日',
      rule: `「YYYY/MM/DD」の形式で入力。${w.pair}同じ受渡日を入力。${DELIVERY_DATE_BASE}`,
      example: DELIVERY_DATE_EXAMPLE,
    },
    extra.pairNumber,
    extra.blockType,
    {
      name: '発注主体区分',
      required: 'required',
      description: '入札する発注主体',
      rule: `「需要BG / 発電 / 自己託送BG」のいずれかを入力。${w.pair}同じ区分を入力。広域機関に自社のコードが登録されている区分のみ入力可能。`,
      example: '需要BG',
    },
    extra.area,
    {
      name: '売買',
      required: 'required',
      description: '自社から見た売買の方向',
      rule: `「買」または「売」を入力。${w.separately}入力できる。ブロック内のすべてのコマに同じ方向が適用される。`,
      example: '売',
    },
    {
      name: '価格 (円/kWh)',
      required: 'required',
      description: '売買したい価格 (円/kWh)',
      rule: `${PRICE_RANGE}${w.separately}入力できる (価格は各ブロックに 1 つ)。${PRICE_NOTES}`,
      example: '10.00',
    },
    {
      name: '排出係数紐付け',
      required: 'optional',
      description: 'JEPX の約定に、特定契約の電源の排出係数を紐付けるか',
      rule: `${EMISSION_BASE}${w.pair}同じ値を入力。`,
      example: '1',
    },
    {
      name: 'JEPX自由記述欄',
      required: 'optional',
      description: 'JEPX へ送信される備考',
      rule: `${JEPX_FREE_TEXT_BASE}${w.pair}同じ値を入力。`,
      example: 'A',
    },
    {
      name: 'メモ',
      required: 'optional',
      description: '社内向けのメモ (JEPX へは送信しない)',
      rule: `100文字以内で入力。${w.pair}同じ値を入力。`,
      example: extra.memoExample,
    },
    {
      name: 'コマ1〜コマ48',
      required: 'optional',
      description: '各コマ (30 分単位) の入札量 (MW)',
      rule: `${SLOT_BASE}${extra.slotOverlap}`,
      example: '100',
    },
  ]
}

/** 通常ブロック (56 列)。 */
export const BLOCK_BID_STANDARD_COLUMN_GUIDE: ColumnGuideEntry[] = [
  {
    name: '受渡日',
    required: 'required',
    description: '入札する受渡日',
    rule: `「YYYY/MM/DD」の形式で入力。${DELIVERY_DATE_BASE}`,
    example: DELIVERY_DATE_EXAMPLE,
  },
  {
    name: '発注主体区分',
    required: 'required',
    description: '入札する発注主体',
    rule: '「需要BG / 発電 / 自己託送BG」のいずれかを入力。広域機関に自社のコードが登録されている区分のみ入力可能。行ごとに異なる区分を入力できる。',
    example: '需要BG',
  },
  {
    name: 'エリア',
    required: 'required',
    description: '入札するエリア',
    rule: `「${AREA_LIST}」のいずれかを入力。同じ行の発注主体区分で発注でき、かつ業務エリア設定に含まれるエリアのみ入力可能。`,
    example: '東京',
  },
  {
    name: '売買',
    required: 'required',
    description: '自社から見た売買の方向',
    rule: '「買」または「売」を入力。ブロック内のすべてのコマに同じ方向が適用される。',
    example: '売',
  },
  {
    name: '価格 (円/kWh)',
    required: 'required',
    description: '売買したい価格 (円/kWh)',
    rule: `${PRICE_RANGE}価格はブロックに 1 つ。${PRICE_NOTES}`,
    example: '10.00',
  },
  {
    name: '排出係数紐付け',
    required: 'optional',
    description: 'JEPX の約定に、特定契約の電源の排出係数を紐付けるか',
    rule: EMISSION_BASE,
    example: '1',
  },
  {
    name: 'JEPX自由記述欄',
    required: 'optional',
    description: 'JEPX へ送信される備考',
    rule: JEPX_FREE_TEXT_BASE,
    example: 'A',
  },
  {
    name: 'メモ',
    required: 'optional',
    description: '社内向けのメモ (JEPX へは送信しない)',
    rule: '100文字以内で入力。',
    example: '火力の余剰分',
  },
  {
    name: 'コマ1〜コマ48',
    required: 'optional',
    description: '各コマ (30 分単位) の入札量 (MW)',
    rule: SLOT_BASE,
    example: '100',
  },
]

/** リンクブロック (58 列)。親 = P、子 = C。 */
export const BLOCK_BID_LINK_COLUMN_GUIDE: ColumnGuideEntry[] = pairedGuide(
  {
    pair: '親と子の行には',
    separately: '親と子で別々に',
  },
  {
    pairNumber: {
      name: 'ペア番号',
      required: 'required',
      description: '親と子の行を 1 件のリンクブロックにまとめるための番号',
      rule: '20文字以内で入力。親ブロックの行と子ブロックの行に同じ番号を入力する。同じ番号の行は P 1 行・C 1 行の 2 行にする。',
      example: '1',
    },
    blockType: {
      name: 'ブロック種別',
      required: 'required',
      description: 'リンクブロックの親か子か',
      rule: '親ブロックは「P」、子ブロックは「C」を入力。子ブロックは親ブロックが約定した場合にだけ約定する。',
      example: 'P',
    },
    area: {
      name: 'エリア',
      required: 'required',
      description: '入札するエリア',
      rule: `「${AREA_LIST}」のいずれかを入力。親と子の行は同じエリアでも別のエリアでもよい。同じ行の発注主体区分で発注でき、かつ業務エリア設定に含まれるエリアのみ入力可能。`,
      example: '東京',
    },
    memoExample: '朝間スポット売り',
    slotOverlap: '親と子のコマは重なってもよい。',
  },
)

/** ループ (58 列)。A と B。 */
export const BLOCK_BID_LOOP_COLUMN_GUIDE: ColumnGuideEntry[] = pairedGuide(
  {
    pair: 'A と B の行には',
    separately: 'A と B で別々に',
  },
  {
    pairNumber: {
      name: 'ペア番号',
      required: 'required',
      description: 'A と B の行を 1 件のループブロックにまとめるための番号',
      rule: '20文字以内で入力。A の行と B の行に同じ番号を入力する。同じ番号の行は A 1 行・B 1 行の 2 行にする。',
      example: '1',
    },
    blockType: {
      name: 'ブロック種別',
      required: 'required',
      description: 'ループブロックの A か B か',
      rule: '「A」または「B」を入力。A と B は両方が約定するか、両方が約定しないかのどちらかになる。',
      example: 'A',
    },
    area: {
      name: 'エリア',
      required: 'required',
      description: '入札するエリア',
      rule: `「${AREA_LIST}」のいずれかを入力。同じ行の発注主体区分で発注でき、かつ業務エリア設定に含まれるエリアのみ入力可能。A と B の行は同じエリアでも別のエリアでもよい。`,
      example: '東京',
    },
    memoExample: '東西の値差狙い',
    slotOverlap: 'A と B のコマは重なってもよい。',
  },
)

/*
 * 英語表示の記入ガイド表 (spec §3「表示言語が英語のとき」、 文言の正本は spec
 * 「付録: 表示文言（英語）」の第3節の行、 #9332 問 2)。 列名は §2 の英語ヘッダー名
 * (`~/models/bidCsvHeader` の `*_EN`)、 区分値の記入例は英語の値。 日本語版と同じく
 * 言語でガイド行ごと選ぶ (#8673 の方針)。
 */
const AREA_LIST_EN =
  'Hokkaido / Tohoku / Tokyo / Chubu / Hokuriku / Kansai / Chugoku / Shikoku / Kyushu'

const DELIVERY_DATE_BASE_EN =
  'Delivery dates past the bid deadline (10:00 on the day before the delivery date) cannot be entered. Bids submitted before bidding opens (0:00, 11 days before the delivery date) are held as reserved bids and sent to JEPX when bidding opens. One file can contain multiple delivery dates.'
const DELIVERY_DATE_EXAMPLE_EN =
  "The next day's date (until 10:00 today. Example: 2026/09/01 if today is 2026/08/31)"

const PRICE_FEE_EN =
  'Market orders cannot be specified. When automatic fee adjustment is OFF, the price you enter is placed with JEPX as the order price. When it is ON, enter the price you want to trade at including the fee (the desired price). The bid is placed with JEPX at the order price, which is the desired price with the fee added or subtracted, and the order price is shown on the confirmation screen before import. The upper and lower price limits in the bid limits are checked against the order price.'

const EMISSION_BASE_EN =
  'Enter 1 only when linking the emission factor of a specific-contract power source (your retailer registration number must be registered in the company information). Leave blank when not linking.'

const JEPX_FREE_TEXT_BASE_EN =
  'When emission factor linking is 1, enter 1 uppercase half-width letter (A-Z) to use in the identification comment (the system builds the identification comment in the format specified by JEPX from your retailer registration number and this letter, and sends it to JEPX. You can find your retailer registration number in the company information on the System management screen). When emission factor linking is blank, enter free text of up to 70 characters. When emission factor linking is 1, free text cannot be added.'

const SLOT_BASE_EN =
  'For each slot to bid in, enter a value from 0.1 to 9999.9, with up to 1 decimal place (no sign). Leave slots outside the block blank. At least 4 consecutive slots (2 hours) are required, with no blank slots in between. You can enter 0 for a slot inside the block that you do not bid in, but the first and last slots cannot be 0. Each slot can have a different quantity. Enter a value within the maximum quantity in the bid limits.'

const ISSUER_TYPES_EN =
  'Enter one of Demand BG / Generation / Self-wheeling BG.'
const ISSUER_REGISTERED_EN =
  "Only issuer types for which your company's code is registered in OCCTO can be entered."
const AREA_ALLOWED_EN =
  'Only areas where orders can be placed with the issuer type in the same row, and that are included in your business area settings, can be entered.'

/** 英語表示のコマ列 1 行の列名 (`Slot1`〜`Slot48` を 1 行にまとめる)。 */
export const BLOCK_BID_SLOT_GUIDE_NAME_EN = 'Slot1-Slot48'

type PairWordingEn = {
  /** 「the parent and child」「the A and B」 (行に同じ値を入れる対象)。 */
  rows: string
  /** 「the parent and child」「A and B」 (別々に入力できる対象)。 */
  separately: string
}

function pairedGuideEn(
  w: PairWordingEn,
  extra: {
    pairNumber: ColumnGuideEntry
    blockType: ColumnGuideEntry
    area: ColumnGuideEntry
    memoExample: string
    slotOverlap: string
  },
): ColumnGuideEntry[] {
  const same = `Enter the same value in ${w.rows} rows.`
  return [
    {
      name: 'DeliveryDate',
      required: 'required',
      description: 'Delivery date to bid for',
      rule: `Enter in YYYY/MM/DD format. Enter the same delivery date in ${w.rows} rows. ${DELIVERY_DATE_BASE_EN}`,
      example: DELIVERY_DATE_EXAMPLE_EN,
    },
    extra.pairNumber,
    extra.blockType,
    {
      name: 'IssuerType',
      required: 'required',
      description: 'Issuer that places the bid',
      rule: `${ISSUER_TYPES_EN} Enter the same issuer type in ${w.rows} rows. ${ISSUER_REGISTERED_EN}`,
      example: 'Demand BG',
    },
    extra.area,
    {
      name: 'BuySell',
      required: 'required',
      description: "Buy/sell direction from your company's side",
      rule: `Enter Buy or Sell. You can enter different values for ${w.separately}. The same direction applies to all slots in the block.`,
      example: 'Sell',
    },
    {
      name: 'OrderPrice',
      required: 'required',
      description: 'Price at which you want to buy or sell (JPY/kWh)',
      rule: `Enter a value from 0.01 to 999, with up to 2 decimal places. You can enter different prices for ${w.separately} (each block has one price). ${PRICE_FEE_EN}`,
      example: '10.00',
    },
    {
      name: 'EmissionFactorLinking',
      required: 'optional',
      description:
        'Whether to link the emission factor of a specific-contract power source to the JEPX execution',
      rule: `${EMISSION_BASE_EN} ${same}`,
      example: '1',
    },
    {
      name: 'JEPXFreeText',
      required: 'optional',
      description: 'Note sent to JEPX',
      rule: `${JEPX_FREE_TEXT_BASE_EN} ${same}`,
      example: 'A',
    },
    {
      name: 'Memo',
      required: 'optional',
      description: 'Internal memo (not sent to JEPX)',
      rule: `Enter up to 100 characters. ${same}`,
      example: extra.memoExample,
    },
    {
      name: BLOCK_BID_SLOT_GUIDE_NAME_EN,
      required: 'optional',
      description: 'Bid quantity for each slot (30 minutes) (MW)',
      rule: `${SLOT_BASE_EN} ${extra.slotOverlap}`,
      example: '100',
    },
  ]
}

/** 通常ブロック (56 列) の英語表示。 */
export const BLOCK_BID_STANDARD_COLUMN_GUIDE_EN: ColumnGuideEntry[] = [
  {
    name: 'DeliveryDate',
    required: 'required',
    description: 'Delivery date to bid for',
    rule: `Enter in YYYY/MM/DD format. ${DELIVERY_DATE_BASE_EN}`,
    example: DELIVERY_DATE_EXAMPLE_EN,
  },
  {
    name: 'IssuerType',
    required: 'required',
    description: 'Issuer that places the bid',
    rule: `${ISSUER_TYPES_EN} ${ISSUER_REGISTERED_EN} Each row can have a different issuer type.`,
    example: 'Demand BG',
  },
  {
    name: 'Area',
    required: 'required',
    description: 'Area to bid in',
    rule: `Enter one of ${AREA_LIST_EN}. ${AREA_ALLOWED_EN}`,
    example: 'Tokyo',
  },
  {
    name: 'BuySell',
    required: 'required',
    description: "Buy/sell direction from your company's side",
    rule: 'Enter Buy or Sell. The same direction applies to all slots in the block.',
    example: 'Sell',
  },
  {
    name: 'OrderPrice',
    required: 'required',
    description: 'Price at which you want to buy or sell (JPY/kWh)',
    rule: `Enter a value from 0.01 to 999, with up to 2 decimal places. Each block has one price. ${PRICE_FEE_EN}`,
    example: '10.00',
  },
  {
    name: 'EmissionFactorLinking',
    required: 'optional',
    description:
      'Whether to link the emission factor of a specific-contract power source to the JEPX execution',
    rule: EMISSION_BASE_EN,
    example: '1',
  },
  {
    name: 'JEPXFreeText',
    required: 'optional',
    description: 'Note sent to JEPX',
    rule: JEPX_FREE_TEXT_BASE_EN,
    example: 'A',
  },
  {
    name: 'Memo',
    required: 'optional',
    description: 'Internal memo (not sent to JEPX)',
    rule: 'Enter up to 100 characters.',
    example: 'Surplus thermal power',
  },
  {
    name: BLOCK_BID_SLOT_GUIDE_NAME_EN,
    required: 'optional',
    description: 'Bid quantity for each slot (30 minutes) (MW)',
    rule: SLOT_BASE_EN,
    example: '100',
  },
]

/** リンクブロック (58 列) の英語表示。 */
export const BLOCK_BID_LINK_COLUMN_GUIDE_EN: ColumnGuideEntry[] = pairedGuideEn(
  { rows: 'the parent and child', separately: 'the parent and child' },
  {
    pairNumber: {
      name: 'PairNumber',
      required: 'required',
      description:
        'Number that combines the parent and child rows into one link block',
      rule: 'Enter up to 20 characters. Enter the same number in the parent block row and the child block row. Use each number in exactly 2 rows: 1 P row and 1 C row.',
      example: '1',
    },
    blockType: {
      name: 'BlockType',
      required: 'required',
      description: 'Whether the row is the parent or child of the link block',
      rule: 'Enter P for the parent block and C for the child block. The child block is filled only when the parent block is filled.',
      example: 'P',
    },
    area: {
      name: 'Area',
      required: 'required',
      description: 'Area to bid in',
      rule: `Enter one of ${AREA_LIST_EN}. The parent and child rows can have the same area or different areas. ${AREA_ALLOWED_EN}`,
      example: 'Tokyo',
    },
    memoExample: 'Morning day-ahead sale',
    slotOverlap: 'The parent and child slots can overlap.',
  },
)

/** ループ (58 列) の英語表示。 */
export const BLOCK_BID_LOOP_COLUMN_GUIDE_EN: ColumnGuideEntry[] = pairedGuideEn(
  { rows: 'the A and B', separately: 'A and B' },
  {
    pairNumber: {
      name: 'PairNumber',
      required: 'required',
      description: 'Number that combines the A and B rows into one loop block',
      rule: 'Enter up to 20 characters. Enter the same number in the A row and the B row. Use each number in exactly 2 rows: 1 A row and 1 B row.',
      example: '1',
    },
    blockType: {
      name: 'BlockType',
      required: 'required',
      description: 'Whether the row is A or B of the loop block',
      rule: 'Enter A or B. A and B are either both filled or both unfilled.',
      example: 'A',
    },
    area: {
      name: 'Area',
      required: 'required',
      description: 'Area to bid in',
      rule: `Enter one of ${AREA_LIST_EN}. ${AREA_ALLOWED_EN} The A and B rows can have the same area or different areas.`,
      example: 'Tokyo',
    },
    memoExample: 'Targeting the east-west price spread',
    slotOverlap: 'The A and B slots can overlap.',
  },
)

const COLUMN_GUIDES: Record<
  BlockBidCsvKind,
  { ja: ColumnGuideEntry[]; en: ColumnGuideEntry[] }
> = {
  standard: {
    ja: BLOCK_BID_STANDARD_COLUMN_GUIDE,
    en: BLOCK_BID_STANDARD_COLUMN_GUIDE_EN,
  },
  link: { ja: BLOCK_BID_LINK_COLUMN_GUIDE, en: BLOCK_BID_LINK_COLUMN_GUIDE_EN },
  loop: { ja: BLOCK_BID_LOOP_COLUMN_GUIDE, en: BLOCK_BID_LOOP_COLUMN_GUIDE_EN },
}

/** 種別と表示言語に応じた記入ガイド表 (spec §3)。 */
export function blockBidCsvColumnGuide(
  type: BlockBidCsvKind,
  isEnglish: boolean,
): ColumnGuideEntry[] {
  const guides = COLUMN_GUIDES[type]
  return isEnglish ? guides.en : guides.ja
}
